import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useOperatesModule } from "@/shared/hooks/useOperatesModule";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
} from "../../../shared/api/clinicFeatures";

interface Props {
  encounterId: string;
  hasVitals: boolean;
  diagnosisCount: number;
  prescriptionItemCount: number;
  attachmentCount: number;
}

const tabClass = (isActive: boolean) =>
  cn(
    "inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
    isActive
      ? "border-primary text-foreground"
      : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
  );

/** Sticky tab strip across encounter sub-pages. Counts surface at a glance. */
export function EncounterTabs({
  encounterId,
  hasVitals,
  diagnosisCount,
  prescriptionItemCount,
  attachmentCount,
}: Props) {
  const features = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  // Ẩn tab Đơn thuốc khi phòng khám tắt kê đơn, hoặc khi phòng khám không kê đơn trên Addy / gói
  // không có Đơn thuốc (ADR-0053: Khám bệnh gói nào cũng có, Đơn thuốc thì không). Một NavLink
  // không tốn request nào nên dùng `operates` lạc quan.
  const prescribing = useOperatesModule("PRESCRIPTIONS");
  const showPrescription = features.data?.prescribeEnabled !== false && prescribing.operates;
  return (
    <nav
      aria-label="Mục lần khám"
      className="sticky top-0 z-10 flex items-center gap-1 overflow-x-auto border-b border-border bg-background"
    >
      <NavLink
        end
        to={`/encounters/${encounterId}`}
        className={({ isActive }) => tabClass(isActive)}
      >
        Tổng quan
      </NavLink>
      <NavLink
        to={`/encounters/${encounterId}/vitals`}
        className={({ isActive }) => tabClass(isActive)}
      >
        Sinh hiệu
        {hasVitals && (
          <Badge variant="success" className="h-5 w-5 justify-center p-0" aria-label="Đã ghi nhận">
            <Check className="h-3 w-3" aria-hidden="true" />
          </Badge>
        )}
      </NavLink>
      <NavLink
        to={`/encounters/${encounterId}/notes`}
        className={({ isActive }) => tabClass(isActive)}
      >
        SOAP
      </NavLink>
      <NavLink
        to={`/encounters/${encounterId}/diagnoses`}
        className={({ isActive }) => tabClass(isActive)}
      >
        Chẩn đoán
        {diagnosisCount > 0 && (
          <Badge variant="secondary" className="min-w-5 justify-center px-1.5">
            {diagnosisCount}
          </Badge>
        )}
      </NavLink>
      {showPrescription && (
        <NavLink
          to={`/encounters/${encounterId}/prescription`}
          className={({ isActive }) => tabClass(isActive)}
        >
          Đơn thuốc
          {prescriptionItemCount > 0 && (
            <Badge variant="secondary" className="min-w-5 justify-center px-1.5">
              {prescriptionItemCount}
            </Badge>
          )}
        </NavLink>
      )}
      <NavLink
        to={`/encounters/${encounterId}/attachments`}
        className={({ isActive }) => tabClass(isActive)}
      >
        Đính kèm
        {attachmentCount > 0 && (
          <Badge variant="secondary" className="min-w-5 justify-center px-1.5">
            {attachmentCount}
          </Badge>
        )}
      </NavLink>
    </nav>
  );
}
